import type { VehiclesTabAction } from './actions';
import { ActionTypes } from './actionTypes';
import type { EtaVehicle } from './types';

import type { Destination, UUID, VehicleGroup } from '@cobliteam/common-types';

import { type WebData, webDataError, webDataFetching, webDataNotAsked, webDataSuccess } from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';

import { rootReducer } from '../HOR';

export interface VehiclesTabState {
  isEtaDialogOpen: boolean;
  etaVehiclesWebData: WebData<EtaVehicle[]>;
  etaRouteWebData: WebData<google.maps.DirectionsRoute>;
  destination?: Destination;
  etaDevice?: DeviceFragment;
  appliedFiltersWebData: WebData<void>;
  appliedVehicleGroupsFilters: VehicleGroup[];
  appliedVehicleFilters: UUID[];
}

export const vehiclesTabInitialState: VehiclesTabState = {
  isEtaDialogOpen: false,
  etaVehiclesWebData: webDataNotAsked(),
  etaRouteWebData: webDataNotAsked(),
  appliedFiltersWebData: webDataNotAsked(),
  appliedVehicleGroupsFilters: [],
  appliedVehicleFilters: [],
};

const vehiclesReducer = (state: VehiclesTabState = vehiclesTabInitialState, action: VehiclesTabAction): VehiclesTabState => {
  if (!(action.type in ActionTypes)) {
    return state;
  }

  switch (action.type) {
    case ActionTypes.LOAD_ETA_VEHICLES_REQUEST:
      return { ...state, etaVehiclesWebData: webDataFetching() };

    case ActionTypes.LOAD_ETA_VEHICLES_SUCCEEDED:
      const { etaVehicles, destination } = action.payload;

      return {
        ...state,
        etaVehiclesWebData: webDataSuccess(etaVehicles),
        destination,
      };

    case ActionTypes.LOAD_ETA_VEHICLES_FAILED:
      return { ...state, etaVehiclesWebData: webDataError() };

    case ActionTypes.LOAD_ETA_ROUTE_REQUEST:
      return { ...state, etaRouteWebData: webDataFetching() };

    case ActionTypes.LOAD_ETA_ROUTE_SUCCEEDED:
      return {
        ...state,
        etaRouteWebData: webDataSuccess(action.payload.route),
      };

    case ActionTypes.LOAD_ETA_ROUTE_FAILED:
      return { ...state, etaRouteWebData: webDataError() };

    case ActionTypes.CLEAR_ETA_ROUTE:
      return { ...state, etaRouteWebData: webDataNotAsked() };

    case ActionTypes.OPEN_ETA_DIALOG:
      return {
        ...state,
        isEtaDialogOpen: true,
        etaDevice: action.payload,
      };

    case ActionTypes.CLOSE_ETA_DIALOG:
      return { ...state, isEtaDialogOpen: false };

    case ActionTypes.APPLY_VEHICLE_GROUPS_FILTER_REQUEST:
      return {
        ...state,
        appliedFiltersWebData: webDataFetching(),
        appliedVehicleGroupsFilters: action.payload,
      };

    case ActionTypes.APPLY_VEHICLE_FILTER_REQUEST:
      return {
        ...state,
        appliedFiltersWebData: webDataFetching(),
        appliedVehicleFilters: action.payload,
      };

    case ActionTypes.APPLY_FILTERS_SUCCEEDED:
      return {
        ...state,
        appliedFiltersWebData: webDataSuccess(undefined),
      };

    case ActionTypes.APPLY_FILTERS_FAILED:
      return { ...state, appliedFiltersWebData: webDataError() };

    default:
      return state;
  }
};

export const vehiclesTabReducer = rootReducer([vehiclesReducer], vehiclesTabInitialState);
